import React, { useMemo } from "react";
import { Box } from "@dxp-feed/ui/Box";
import { Columns } from "@dxp-feed/ui/Columns";
import { Text } from "@dxp-feed/ui/Text";
import PopularSliderSection from "./PopularSliderSection";
import { GLOBAL_SEARCH_RESULTS_SECTIONS } from "@/utils/constant";
import { hasProfanity } from "@dxp-feed/algolia/dist/services/profanity";
import { replaceAEMStaticText } from "@/helpers/global";
import Loader from "@/components/Loader";
import { useTrendingItems } from "@/utils/hooks/useTrendingItems";

interface NoResultsProps {
  searchQuery: string;
  currentTab: GLOBAL_SEARCH_RESULTS_SECTIONS;
  totalCount: number;
  className?: string;
  showControls?: boolean;
  isGlobalSearch?: boolean;
  isSearchPage?: boolean;
  setDropDown?: (value: boolean) => void;
  setIsPanelOpen?: any;
  slidesPerView?: {
    base: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
    xxl: number;
  };
  staticContent: {
    noProductResultsMsg?: string;
    noProductResultsMsgForProfanity?: string;
    noProductResultsSubMsg?: string;
    noRecipeResultsMsg?: string;
    noRecipeResultsMsgForProfanity?: string;
    noRecipeResultsSubMsg?: string;
    noArticleResultsMsg?: string;
    noArticleResultsMsgForProfanity?: string;
    noArticleResultsSubMsg?: string;
    noResultsMsg: string;
    noResultsSubMsg: string;
    noResultsProfanityMsg: string;
    productsCarouselTitle?: string;
    productsCarouselCTA?: string;
    articlesCarouselTitle?: string;
    articlesCarouselCTA?: string;
    recipesCarouselTitle?: string;
    recipesCarouselCTA?: string;
  } | null;
}
export default function NoResults({
  searchQuery,
  currentTab,
  totalCount,
  showControls = true,
  slidesPerView,
  setDropDown,
  className = "",
  isGlobalSearch = false,
  staticContent,
  isSearchPage,
  setIsPanelOpen,
}: Readonly<NoResultsProps>) {
  const { data: popularItems } = useTrendingItems(true);

  const slides = useMemo(
    () => [
      {
        id: 1,
        title: staticContent?.productsCarouselTitle,
        titleCTA: staticContent?.productsCarouselCTA,
        key: "products",
        noResultsMsg: staticContent?.noProductResultsMsg,
        noResultsMsgForProfanity:
          staticContent?.noProductResultsMsgForProfanity,
        noResultsSubMsg: staticContent?.noProductResultsSubMsg,
        type: GLOBAL_SEARCH_RESULTS_SECTIONS.PRODUCT,
        data: !!popularItems?.products?.nbHits,
        slideCount: isGlobalSearch ? 5 : 18,
        isVisible: function (): boolean {
          return totalCount < 1 || currentTab === this.key.toLowerCase();
        },
      },
      {
        id: 2,
        title: staticContent?.recipesCarouselTitle,
        titleCTA: staticContent?.recipesCarouselCTA,
        key: "recipes",
        noResultsMsg: staticContent?.noRecipeResultsMsg,
        noResultsMsgForProfanity: staticContent?.noRecipeResultsMsgForProfanity,
        noResultsSubMsg: staticContent?.noRecipeResultsSubMsg,
        type: GLOBAL_SEARCH_RESULTS_SECTIONS.RECIPE,
        data: !!popularItems?.recipes?.nbHits,
        slideCount: 12,
        isVisible: function (): boolean {
          return totalCount < 1 || currentTab === this.key.toLowerCase();
        },
      },
      {
        id: 3,
        title: staticContent?.articlesCarouselTitle,
        titleCTA: staticContent?.articlesCarouselCTA,
        key: "articles",
        noResultsMsg: staticContent?.noArticleResultsMsg,
        noResultsMsgForProfanity:
          staticContent?.noArticleResultsMsgForProfanity,
        noResultsSubMsg: staticContent?.noArticleResultsSubMsg,
        type: GLOBAL_SEARCH_RESULTS_SECTIONS.ARTICLE,
        data: !!popularItems?.articles?.nbHits,
        slideCount: 12,
        isVisible: function (): boolean {
          return totalCount < 1 || currentTab === this.key.toLowerCase();
        },
      },
    ],
    [staticContent, popularItems, isGlobalSearch, totalCount, currentTab]
  );

  const currentSlideData = useMemo(
    () => slides.find((item) => item.type == currentTab),
    [slides, currentTab]
  );

  const searchResultFor = useMemo(
    () => slides.filter((item) => item.isVisible()).length > 1,
    [slides]
  );
  return (
    <Columns className={`col-12 ${className}`}>
      <Box
        as="div"
        className={`no-result-tabs w-full text-center ${searchResultFor ? "my-0 xs:my-[24px] 2md:my-[32px] py-[24px]" : "!mb-0 mt-[24px] lg:mt-0"}  `}
      >
        <Text
          as={searchResultFor ? "h1" : "h2"}
          className={`text-center break-words result-heading font-sans w-full ${searchResultFor ? "text-2xl  xs:text-3xl" : "text-md  xs:text-xl"}  `}
        >
          {!hasProfanity(searchQuery) ? (
            <>
              {!searchResultFor && !isGlobalSearch ? (
                <Text as="span" className="block md:inline">
                  {replaceAEMStaticText(currentSlideData?.noResultsMsg, {
                    searchQuery,
                    resultsCount: totalCount,
                  })}
                </Text>
              ) : (
                <Text as="span" className="block md:inline">
                  {replaceAEMStaticText(staticContent?.noResultsMsg, {
                    searchQuery,
                    resultsCount: totalCount,
                  })}
                </Text>
              )}
            </>
          ) : (
            <Text as="span" className="block md:inline">
              {!searchResultFor && !isGlobalSearch
                ? replaceAEMStaticText(
                    currentSlideData?.noResultsMsgForProfanity,
                    {
                      searchQuery,
                      resultsCount: totalCount,
                    }
                  )
                : replaceAEMStaticText(staticContent?.noResultsProfanityMsg, {
                    searchQuery,
                    resultsCount: totalCount,
                  })}
            </Text>
          )}
        </Text>
        <Text
          as="p"
          className={`text-center  w-full ${searchResultFor ? "text-base mt-[14px]" : "text-body mt-[8px]"}`}
        >
          {searchResultFor || isGlobalSearch
            ? replaceAEMStaticText(staticContent?.noResultsSubMsg, {
                searchQuery,
                resultsCount: totalCount,
              })
            : replaceAEMStaticText(currentSlideData?.noResultsSubMsg, {
                searchQuery,
                resultsCount: totalCount,
              })}
        </Text>
      </Box>

      {searchResultFor && <hr className="border-grey350"></hr>}

      {!staticContent ? (
        <Loader />
      ) : (
        <>
          {slides
            ?.filter((item) => item.isVisible())
            ?.map(
              (item) =>
                item?.data && (
                  <PopularSliderSection
                    setIsPanelOpen={setIsPanelOpen}
                    type={item.type}
                    key={item.id}
                    title={item.title!}
                    titleCTA={item.titleCTA!}
                    setDropDown={setDropDown}
                    isGlobalSearch={isGlobalSearch}
                    slideCount={item.slideCount}
                    slidesPerView={slidesPerView}
                    showControls={showControls}
                    isSearchPage={isSearchPage}
                  />
                )
            )}
        </>
      )}
    </Columns>
  );
}
